'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { Logtail } from '@logtail/browser'
import posthog from 'posthog-js'
import { setupConsoleOverride, restoreConsole } from '../lib/console-override'
import { setLogtailLogger } from '../lib/api-logger'
import { useAuth } from '@/providers/auth-provider'

interface LogtailContextType {
  logtail: Logtail | null
  isReady: boolean
  getLogContext: () => object
}

const LogtailContext = createContext<LogtailContextType>({
  logtail: null,
  isReady: false,
  getLogContext: () => ({}),
})

export const useLogtail = () => {
  const context = useContext(LogtailContext)
  if (!context) {
    throw new Error('useLogtail must be used within a LogtailProvider')
  }
  return context
}

export const LogtailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [logtail, setLogtail] = useState<Logtail | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [config, setConfig] = useState<{ env?: string } | null>(null)
  const [clientIp, setClientIp] = useState<string | null>(null)
  const { user } = useAuth()

  const getLogContext = () => ({
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV || 'development',
    env: config?.env || process.env.ENV || 'local',
    user: user?.email || null,
    ip: clientIp || null,
  })

  useEffect(() => {
    const initializeLogtail = async () => {
      // If logtail is already initialized, just update console override with new context
      if (logtail) {
        setupConsoleOverride(logtail, getLogContext)
        return
      }

      try {
        const [configResponse, ipResponse] = await Promise.all([
          fetch('/api/config'),
          fetch('/api/client-ip'),
        ])
        const fetchedConfig = await configResponse.json()
        const ipData = await ipResponse.json().catch(() => ({ ip: null }))
        setConfig(fetchedConfig)
        setClientIp(ipData.ip || null)
        if (ipData.ip) {
          posthog.register({ clientIp: ipData.ip })
        }

        if (fetchedConfig.logtailToken) {
          const logtailInstance = new Logtail(fetchedConfig.logtailToken, {
            endpoint: fetchedConfig.logtailEndpoint,
          })
          setLogtail(logtailInstance)
          setIsReady(true)

          // Setup console override after Logtail is ready with context
          setupConsoleOverride(logtailInstance, getLogContext)

          // Setup API logger with Logtail instance
          setLogtailLogger(logtailInstance)

          logtailInstance.info(
            'Logtail initialized successfully with console override and API logging',
            {
              ...getLogContext(),
              source: 'logtail-provider',
            },
          )
        }
      } catch (error) {
        console.error('Failed to initialize Logtail:', error)
        setIsReady(true)
      }
    }

    initializeLogtail()

    // Cleanup function to restore console on unmount
    return () => {
      // restoreConsole() // Don't restore console on unmount if it's a global provider
    }
  }, [user, logtail, config?.env])

  return (
    <LogtailContext.Provider value={{ logtail, isReady, getLogContext }}>
      {children}
    </LogtailContext.Provider>
  )
}
